import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUpdateServer } from '@/hooks/use-update-server';

const MAX_NOTES = 2000;

/**
 * Inline notes editor on the detail panel. Reads `server.notes`, edits it via
 * PATCH /api/admin/fleet/servers/:name. `support`-role users see the value
 * but the Edit button is disabled — the API enforces RBAC regardless.
 */
export function NotesEditor({
  serverName,
  notes,
}: {
  serverName: string;
  notes: string | null;
}): JSX.Element {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  // The last value persisted by this editor — wins over the `notes` prop until
  // the parent's detail query re-renders with the server truth.
  const [saved, setSaved] = useState<string | null>(null);
  const mutation = useUpdateServer();

  const current = saved ?? notes;

  function startEdit(): void {
    setDraft(current ?? '');
    setEditing(true);
  }

  function save(): void {
    const next = draft.trim();

    mutation.mutate(
      { name: serverName, patch: { notes: next } },
      {
        onSuccess: () => {
          toast.success('Notes saved.');
          setSaved(next.length > 0 ? next : '');
          setEditing(false);
        },
        onError: (err) => toast.error(`Couldn't save notes: ${err.message}`),
      },
    );
  }

  if (!editing) {
    return (
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Notes</span>
          {isAdmin ? (
            <Button variant="ghost" size="sm" onClick={startEdit}>
              Edit notes
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="ghost" size="sm" disabled>
                    Edit notes
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Read-only: admin role required</TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className="text-sm">
          {current ? current : <span className="text-muted-foreground">No notes.</span>}
        </p>
      </div>
    );
  }

  const tooLong = draft.length > MAX_NOTES;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Notes</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {draft.length}/{MAX_NOTES}
        </span>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Operator notes (recorded against this server)"
        rows={4}
        autoFocus
        aria-label="Server notes"
      />
      {tooLong && (
        <p className="mt-1 text-xs text-destructive">Notes must be 2000 characters or fewer.</p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={mutation.isPending || tooLong}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
